import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { CardComponent } from '../../components/card/card.component';
import { BulletComponent } from '../../components/bullet/bullet.component';
import { FileUploadComponent } from '../../components/file-upload/file-upload.component';
import { firstValueFrom, Subscription } from 'rxjs';
import { CardTitleComponent } from '../../components/card-title/card-title.component';
import { FeedbackCardComponent } from '../../components/feedback-card/feedback-card.component';
import { LinkComponent } from '../../components/link/link.component';
import { AnalyticsService } from '../../state/analytics/analytics.service';
import { IconComponent } from '../../components/icon/icon.component';
import { GuidanceService } from '../../api/guidance/guidance.service';
import { DownloadUtil } from '../../utils/download/download';
import { GuidanceFormat, GuidanceFormats } from './guidanceFormats';
import { GuidanceItemComponent } from './components/guidance-item.component';
import { WebSocketService } from '../../api/guidance-websocket/guidance-websocket.service';
import { TargetFormat } from '../../api/guidance/guidance.service.interface';
import { ToastService } from '../../state/toast/toast.service';
import { RibbonComponent } from '../../components/ribbon/ribbon.component';
import { InfoHoverComponent } from '../../components/info-hover/info-hover.component';
import { sanitizeFilename } from '../../utils/file-name/file-name';
import { validUpload } from '../../utils/upload/upload';

@Component({
  selector: 'app-guidance-page',
  imports: [
    CardComponent,
    BulletComponent,
    LinkComponent,
    CardTitleComponent,
    FileUploadComponent,
    FeedbackCardComponent,
    IconComponent,
    GuidanceItemComponent,
    RibbonComponent,
    InfoHoverComponent,
  ],
  templateUrl: './guidance-page.component.html',
  styleUrl: './guidance-page.component.css',
})
export class GuidancePageComponent implements OnInit, OnDestroy {
  private analytics = inject(AnalyticsService);
  private guidance = inject(GuidanceService);
  private webSocketService = inject(WebSocketService);
  private toastService = inject(ToastService);

  protected file = signal<File | null>(null);
  protected targets = signal<GuidanceFormats>({ generic: [], company: [] });
  protected inputFormats = signal<string[]>([$localize`Loading...`]);
  protected isConverting = signal(false);
  private downloadUrl = '';
  private downloadName = '';
  private subscription!: Subscription;

  async ngOnInit() {
    const messages$ = await this.webSocketService.connect();
    this.subscription = messages$.subscribe(async ({ status, message }) => {
      if (status === 'completed') {
        this.isConverting.set(false);
        const blob = await firstValueFrom(this.guidance.downloadFile(this.downloadUrl));
        DownloadUtil.downloadFile(blob, this.downloadName);
        this.downloadUrl = '';
        this.track('Download');
        this.toastService.showToast({ type: 'success', message: $localize`Conversion finished and downloaded` });
      }
      if (status === 'failed') {
        this.isConverting.set(false);
        this.downloadUrl = '';
        this.toastService.showToast({ type: 'error', message: $localize`Error: ${message}`, duration: 0 });
      }
    });

    const targetFormats = await firstValueFrom(this.guidance.getTargetFormat());
    const inputFormats = await firstValueFrom(this.guidance.getInputFormat());
    this.inputFormats.set([
      ...new Set(
        inputFormats
          .filter((format) => format.id !== 'Auto')
          .flatMap((format) => format.formats)
          .sort(),
      ),
    ]);
    this.targets.set(this.formatTargets(targetFormats));
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
    this.webSocketService.close();
  }

  async onFileSelected(file: File | null) {
    this.file.set(file);
  }

  async upload(id: string) {
    const file = this.file();
    if (!file || this.isConverting() || !(await validUpload(file, this.toastService))) return;
    this.toastService.showToast({ type: 'info', message: $localize`Conversion job started` });

    this.track('Upload');
    this.isConverting.set(true);
    const { upload_url, download_url } = await firstValueFrom(this.guidance.createJob(id));
    this.downloadUrl = download_url;
    this.downloadName = `${sanitizeFilename(file.name, true)}_${id}.zip`;
    await firstValueFrom(this.guidance.uploadFile(file, upload_url));
  }

  goToFeedback() {
    const location = window.location.href;
    return location.includes('#') ? location : location + '#feedback';
  }

  private track(event: string) {
    this.analytics.trackEvent('guidance-data-converter', event);
  }

  private formatTargets(targetFormats: TargetFormat[]): GuidanceFormats {
    const company: GuidanceFormat[] = [];
    targetFormats
      .filter((targetFormat) => targetFormat.targets.length > 0)
      .forEach((targetFormat) => {
        targetFormat.targets.forEach((target) => {
          // Check if the company format already exists, if not, create it
          if (!company.some((f) => f.name === target.organisation)) {
            company.push({
              name: target.organisation,
              formats: [],
            });
          }
          // Find the company format and add the target to it
          const companyFormat = company.find((f) => f.name === target.organisation);
          if (companyFormat) {
            companyFormat.formats.push({
              name: target.target,
              to: targetFormat.id,
              unit: targetFormat.format,
            });
          }
        });
      });
    company.forEach((c) => c.formats.sort((a, b) => a.name.localeCompare(b.name)));
    company.sort((a, b) => a.name.localeCompare(b.name));

    const generic = targetFormats
      // Take only the formats that are "standardized"
      .filter((targetFormat) => targetFormat.standardized)
      // Format them into needed structure
      .map((format) => ({
        name: format.format.split(' ')[0], // Use the first part of the format name
        formats: [{ name: format.format, to: format.id }],
      }))
      // Group by name, merging formats with the same name
      .reduce((acc, format) => {
        const existing = acc.find((f) => f.name === format.name);
        if (existing) {
          // Merge formats, avoiding duplicates
          format.formats.forEach((f) => {
            if (!existing.formats.some((ef) => ef.name === f.name)) {
              existing.formats.push(f);
            }
          });
        } else {
          acc.push(format);
        }
        return acc;
      }, [] as GuidanceFormat[])
      .map((g) => ({
        ...g,
        formats: [...g.formats].sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    return { generic, company };
  }
}
